/**
 * @description
 * Anthropic service for managing workspaces, API keys, cost and usage analytics.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { 
  AnthropicWorkspace, 
  AnthropicApiKey, 
  AnthropicCostResult, 
  AnthropicUsageResult 
} from "./types";

/**
 * Anthropic service for managing Anthropic integration
 */
export class AnthropicService extends BaseAPIClient {
  /**
   * Get Anthropic organization information
   */
  async getAnthropicOrganization(): Promise<{
    id: string;
    name: string;
    type: "organization";
  }> {
    const data = await this.request<{
      organization: {
        id: string;
        name: string;
        type: "organization";
      };
    }>("/anthropic/organization");
    return data.organization;
  }

  /**
   * Get all Anthropic workspaces
   */
  async getAnthropicWorkspaces(): Promise<AnthropicWorkspace[]> {
    const data = await this.request<{
      workspaces: AnthropicWorkspace[];
    }>("/anthropic/workspaces");
    return data.workspaces;
  }

  /**
   * Create a new Anthropic workspace
   */
  async createAnthropicWorkspace(name: string): Promise<AnthropicWorkspace> {
    const data = await this.request<{
      workspace: AnthropicWorkspace;
    }>("/anthropic/workspaces", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    return data.workspace;
  }

  /**
   * Update an Anthropic workspace
   */
  async updateAnthropicWorkspace(
    id: string,
    updates: { name?: string; display_color?: string },
  ): Promise<AnthropicWorkspace> {
    const data = await this.request<{
      workspace: AnthropicWorkspace;
    }>(`/anthropic/workspaces/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    return data.workspace;
  }

  /**
   * Archive an Anthropic workspace
   */
  async archiveAnthropicWorkspace(id: string): Promise<void> {
    await this.request(`/anthropic/workspaces/${id}/archive`, {
      method: "POST",
    });
  }

  /**
   * Assign clients to an Anthropic workspace
   */
  async assignClientsToWorkspace(
    id: string,
    clientIds: string[],
  ): Promise<void> {
    await this.request(`/anthropic/workspaces/${id}/clients`, {
      method: "POST",
      body: JSON.stringify({ clientIds }),
    });
  }

  /**
   * Get Anthropic API keys
   */
  async getAnthropicApiKeys(): Promise<AnthropicApiKey[]> {
    const data = await this.request<{
      apiKeys: AnthropicApiKey[];
    }>("/anthropic/api-keys");
    return data.apiKeys;
  }

  /**
   * Get Anthropic cost report
   */
  async getAnthropicCost(
    startingAt: string,
    endingAt?: string,
    options?: {
      limit?: number;
      page?: string;
      workspaceIds?: string[];
      apiKeyIds?: string[];
      groupBy?: ("workspace_id" | "api_key_id" | "description")[];
      bucketWidth?: "1d";
    },
  ): Promise<AnthropicCostResult> {
    const params = new URLSearchParams({
      starting_at: startingAt,
    });

    if (endingAt) params.set("ending_at", endingAt);
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.page) params.set("page", options.page);
    if (options?.bucketWidth) params.set("bucket_width", options.bucketWidth);

    if (options?.workspaceIds) {
      options.workspaceIds.forEach((id) =>
        params.append("workspace_ids[]", id),
      );
    }
    if (options?.apiKeyIds) {
      options.apiKeyIds.forEach((id) => params.append("api_key_ids[]", id));
    }
    if (options?.groupBy) {
      options.groupBy.forEach((group) => params.append("group_by[]", group));
    }

    return this.request<AnthropicCostResult>(`/anthropic/cost?${params.toString()}`);
  }

  /**
   * Get Anthropic usage report for Messages API
   */
  async getAnthropicUsage(
    startingAt: string,
    endingAt?: string,
    options?: {
      limit?: number;
      page?: string;
      workspaceIds?: string[];
      apiKeyIds?: string[];
      models?: string[];
      serviceTiers?: ("standard" | "batch" | "priority")[];
      contextWindow?: ("0-200k" | "200k-1M")[];
      groupBy?: (
        | "api_key_id"
        | "workspace_id"
        | "model"
        | "service_tier"
        | "context_window"
      )[];
      bucketWidth?: "1d" | "1h" | "1m";
    },
  ): Promise<AnthropicUsageResult> {
    const params = new URLSearchParams({
      starting_at: startingAt,
    });

    if (endingAt) params.set("ending_at", endingAt);
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.page) params.set("page", options.page);
    if (options?.bucketWidth) params.set("bucket_width", options.bucketWidth);

    if (options?.workspaceIds) {
      options.workspaceIds.forEach((id) =>
        params.append("workspace_ids[]", id),
      );
    }
    if (options?.apiKeyIds) {
      options.apiKeyIds.forEach((id) => params.append("api_key_ids[]", id));
    }
    if (options?.models) {
      options.models.forEach((model) => params.append("models[]", model));
    }
    if (options?.serviceTiers) {
      options.serviceTiers.forEach((tier) =>
        params.append("service_tiers[]", tier),
      );
    }
    if (options?.contextWindow) {
      options.contextWindow.forEach((window) =>
        params.append("context_window[]", window),
      );
    }
    if (options?.groupBy) {
      options.groupBy.forEach((group) => params.append("group_by[]", group));
    }

    return this.request<AnthropicUsageResult>(`/anthropic/usage?${params.toString()}`);
  }
}

// Export singleton instance
export const anthropicService = new AnthropicService();
